/**
 * The Unlinked section of the Backlinks tab (1.J deliverable 16): the other pages that name this
 * one without a link, each with "Link", and "Link all" behind an in-place preview (stage 3).
 */
import { ApiError, type UnlinkedOccurrenceV1 } from '@codex/contracts';
import { Button, Skeleton, TypeGlyph } from '@codex/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { docHref } from '../../editor-services/adapter.js';
import { useNotify } from '../../shell/notices.js';
import { useOpenDoc } from '../../shell/open-doc.js';
import { useLinksApi } from '../api.js';
import { reportFailure } from '../failure.js';
import { backlinksKey, unlinkedKey, useUnlinked } from '../queries.js';
import { glyphFor } from '../story/glyph.js';
import { LinkAllPreview } from './link-all.js';
import { mentionLinkBody } from './unlinked-model.js';

export const NO_UNLINKED_TEXT = 'No unlinked mentions.';
export const UNLINKED_FAILED_TEXT = "Unlinked mentions couldn't be loaded.";
export const VERSION_CONFLICT_NOTICE = 'That page changed. The list is refreshed.';

export function UnlinkedSection({ docId }: { docId: string }) {
  const api = useLinksApi();
  const open = useOpenDoc();
  const notify = useNotify();
  const client = useQueryClient();
  const query = useUnlinked(docId);
  const [busy, setBusy] = useState<string | null>(null);
  const [linkAllOpen, setLinkAllOpen] = useState(false);
  const [linkAllButton, setLinkAllButton] = useState<HTMLButtonElement | null>(null);
  const items = query.data?.pages.flatMap((p) => p.items) ?? [];

  const refetch = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: unlinkedKey(docId) }),
      client.invalidateQueries({ queryKey: backlinksKey(docId) }),
    ]);
  };
  const link = async (item: UnlinkedOccurrenceV1) => {
    setBusy(`${item.sourceDocId}:${item.blockId}:${item.ordinal}`);
    try {
      await api.mentionLink(docId, mentionLinkBody(item));
      await refetch();
    } catch (error) {
      if (error instanceof ApiError && error.code === 'version_conflict') {
        notify(VERSION_CONFLICT_NOTICE);
        await refetch();
      } else {
        reportFailure(notify, error);
      }
    } finally {
      setBusy(null);
    }
  };

  return (
    <section aria-labelledby={`bl-unl-${docId}`}>
      <div className="cx-links-story__head">
        <h3 id={`bl-unl-${docId}`} className="cx-links-tab__heading">
          Unlinked
        </h3>
        {items.length > 0 && (
          <Button
            ref={setLinkAllButton}
            size="sm"
            aria-expanded={linkAllOpen}
            onClick={() => setLinkAllOpen((v) => !v)}
          >
            Link all
          </Button>
        )}
      </div>
      {linkAllOpen && (
        <LinkAllPreview
          docId={docId}
          trigger={linkAllButton}
          onClose={() => setLinkAllOpen(false)}
        />
      )}
      {query.isError ? (
        <div>
          <p role="alert">{UNLINKED_FAILED_TEXT}</p>
          <Button size="sm" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </div>
      ) : query.isPending ? (
        <Skeleton lines={2} label="Loading unlinked mentions" />
      ) : items.length === 0 ? (
        <p className="cx-links-story__empty">{NO_UNLINKED_TEXT}</p>
      ) : (
        <ul
          className="cx-links-tab__list"
          data-testid="links.unlinked.list"
          aria-labelledby={`bl-unl-${docId}`}
        >
          {items.map((item) => {
            const key = `${item.sourceDocId}:${item.blockId}:${item.ordinal}`;
            return (
              <li key={key}>
                <div className="cx-links-tab__item">
                  <TypeGlyph type={glyphFor(item.sourceTypeKey)} />
                  <a
                    href={docHref(item.sourceDocId, item.blockId)}
                    onClick={(e) => open(item.sourceDocId, e)}
                  >
                    {item.sourceTitle}
                  </a>
                  <span className="cx-links-tab__match">{item.text}</span>
                  <Button
                    size="sm"
                    data-testid="links.unlinked.link"
                    disabled={busy === key}
                    onClick={() => void link(item)}
                  >
                    Link
                  </Button>
                </div>
                {item.context && <p className="cx-links-tab__context">{item.context}</p>}
              </li>
            );
          })}
        </ul>
      )}
      {query.hasNextPage && (
        <Button
          size="sm"
          disabled={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        >
          Show more
        </Button>
      )}
    </section>
  );
}
